import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import startCase from 'lodash/startCase';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table from '@decisiv/ui-components/lib/components/Table';
import Link from '@decisiv/ui-components/lib/components/Link';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { Column } from '@decisiv/ui-components/lib/components/Table/types';

import useSort from 'utils/useSort';
import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import ROUTE_NAMES from 'setup/Routing/routeNames';

import ActionCell from './ActionCell';

import { RightAlignedFlex } from '../components/styles';
import { useManagedPlatformAccountsQuery } from './hooks/useManagedPlatformAccountsQuery';

type ManagedFleetType =
  ManagedPlatformAccountsQuery['managedPlatformAccounts']['collection'][0];
type ColumnProperties = Pick<
  ManagedFleetType,
  'id' | 'sentinelTeam' | 'name' | 'totals' | 'sourceId' | 'city' | 'state'
>;

const dealerGroupRegex = /^Dealer Group \d+$/;

const Fleets = () => {
  const { isSuperUser } = useCurrentUserContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data } = useManagedPlatformAccountsQuery({
    variables: {
      sort,
      page,
    },
  });

  const columns: Column<ColumnProperties>[] = [
    {
      name: 'oemSegmentName',
      title: t`OEM Segment Name`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>{get(sentinelTeam, 'oemSegment.name')}</span>
      ),
      sortBy: (direction) => onSort('oemSegmentName', direction),
    },
    {
      name: 'teamName',
      title: t`Team Name`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>{get(sentinelTeam, 'name')}</span>
      ),
      sortBy: (direction) => onSort('teamName', direction),
    },
    {
      name: 'accountName',
      title: t`Account Name`,
      DataCell: ({ rowData: { id, name } }) => {
        if (dealerGroupRegex.test(name)) {
          return <span>{name}</span>;
        }
        return (
          <Link to={ROUTE_NAMES.forAdminManagementAccount(id)} text={name} />
        );
      },
      sortBy: (direction) => onSort('name', direction),
    },
    {
      name: 'accountId',
      title: t`Account ID`,
      DataCell: ({ rowData: { sourceId } }) => <span>{sourceId}</span>,
      sortBy: (direction) => onSort('sourceId', direction),
    },
    {
      name: 'teamType',
      title: t`Account Type`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>{startCase(lowerCase(get(sentinelTeam, 'teamType')))}</span>
      ),
      sortBy: (direction) => onSort('teamType', direction),
    },
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: ({ rowData: { totals } }) => (
        <span>{get(totals, 'enabledAssetsCount')}</span>
      ),
      sortBy: (direction) => onSort('enabledAssetsCount', direction),
    },
    {
      name: 'depots',
      title: t`Depots`,
      DataCell: ({ rowData: { totals } }) => (
        <span>{get(totals, 'enabledAssetGroupingsCount')}</span>
      ),
      sortBy: (direction) => onSort('enabledAssetGroupingsCount', direction),
    },
    {
      name: 'city',
      title: t`City`,
      DataCell: ({ rowData: { city } }) => <span>{city}</span>,
      sortBy: (direction) => onSort('city', direction),
    },
    {
      name: 'state',
      title: t`State`,
      DataCell: ({ rowData: { state } }) => <span>{state}</span>,
      sortBy: (direction) => onSort('state', direction),
    },
    {
      name: 'actions',
      title: t`Actions`,
      DataCell: ({ rowData }) => <ActionCell platformAccount={rowData} />,
      fixed: true,
    },
  ];

  const tableData = data?.managedPlatformAccounts.collection ?? [];
  const pagination = data?.managedPlatformAccounts.pagination;

  return (
    <div>
      <Flex marginX={2} marginY={1} justifyContent="space-between">
        {isSuperUser && (
          <Button text={t`Create Account`} icon={Plus} size="small" />
        )}
        <RightAlignedFlex alignItems="center">
          <P size="small" as="span" shade={1}>
            <Trans>Search Fleet by Name</Trans>
          </P>
          <SearchInput name="searchByFleet" size="small" value="" />
        </RightAlignedFlex>
      </Flex>
      <Table
        data={tableData}
        columns={columns}
        onSort={onSort}
        defaultSort={defaultSort}
        footer={() => (
          <PaginationFooter
            title={t`Accounts`}
            setCurrentPage={setPage}
            totalOnPage={tableData.length}
            page={pagination?.page}
            perPage={pagination?.perPage}
            totalEntries={pagination?.totalEntries}
            totalPages={pagination?.totalPages}
          />
        )}
      />
    </div>
  );
};

export default Fleets;
